import {
  ApplicationRef,
  ChangeDetectorRef,
  ComponentRef,
  Directive,
  EmbeddedViewRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NgImageGalleryPreviewComponent } from '../components/common/ngImageGalleryPreview/ngImageGalleryPreview.component';
import { DocumentTypes } from '../enums/documentTypes';
import { DynamicComponentsService } from '../services/dynamicComponents.service';
import { DocumentViewModel } from '../viewModels/documents/documentViewModel';
import { AttachmentsUtilities } from '../utilities/attachments.utilities';

@Directive({
    selector: '[ngImageGalleryPreview]',
    host: {
        '(click)': 'onClick()',
    },
    standalone: false
})
export class NgImageGalleryPreviewDirective implements OnInit, OnChanges, OnDestroy {
  @Input() image: string;
  @Input() object: DocumentViewModel;
  @Input() attachmentType: DocumentTypes;
  @Input() allDocuments: DocumentViewModel[] = [];
  @Input() contextualInfo: any;
  static ngImageGalleryPreviewComponentRef: ComponentRef<NgImageGalleryPreviewComponent>;

  constructor(
    private readonly dynamicComponentsService: DynamicComponentsService,
    private readonly appRef: ApplicationRef
  ) {
    if (!NgImageGalleryPreviewDirective.ngImageGalleryPreviewComponentRef) {
      this.appendToBody();
    }
  }

  ngOnInit() {
    this.updateInstance();
  }

  ngOnChanges() {
    this.updateInstance();
  }

  ngOnDestroy() {}

  openDirectiveFromComponent(object: DocumentViewModel, attachmentType: DocumentTypes, image: string, allDocuments: DocumentViewModel[] = [], contextualInfo?: any) {
    if (!NgImageGalleryPreviewDirective.ngImageGalleryPreviewComponentRef) {
      this.appendToBody();
    }

    this.image = image;
    this.object = object;
    this.attachmentType = attachmentType;
    this.allDocuments = allDocuments;
    this.contextualInfo = contextualInfo;
    this.updateInstance();
  }

  get ngImageGalleryPreviewInstance(): NgImageGalleryPreviewComponent | null {
    return NgImageGalleryPreviewDirective.ngImageGalleryPreviewComponentRef?.instance || null;
  }

  get ngImageGalleryPreviewChangeDetectorRef(): ChangeDetectorRef | null {
    return NgImageGalleryPreviewDirective.ngImageGalleryPreviewComponentRef?.changeDetectorRef || null;
  }

  updateInstance() {
    if (!this.ngImageGalleryPreviewInstance || !this.ngImageGalleryPreviewChangeDetectorRef) {
      return;
    }

    this.ngImageGalleryPreviewChangeDetectorRef.detectChanges();
  }

  onClick() {
    if (this.attachmentType === DocumentTypes.Image && this.object) {
      if (!NgImageGalleryPreviewDirective.ngImageGalleryPreviewComponentRef) {
        this.appendToBody();
      }

      const imageDocuments = this.getImageDocuments();

      if (imageDocuments.length > 0 && this.ngImageGalleryPreviewInstance) {
        const effectiveContextualInfo = (this.object as any).contextualInfo || this.contextualInfo;
        this.ngImageGalleryPreviewInstance.show(imageDocuments, this.object, effectiveContextualInfo);
      }
    }
  }

  private getImageDocuments(): DocumentViewModel[] {
    if (!this.allDocuments || this.allDocuments.length === 0) {
      return this.object ? [this.object] : [];
    }

    return this.allDocuments.filter(doc => {
      const docType = AttachmentsUtilities.getDocumentType(doc);
      return docType.type === DocumentTypes.Image;
    });
  }

  @HostListener('document:keydown.escape', ['$event']) onEscape(e: Event) {
    e.stopPropagation();
    e.stopImmediatePropagation();

    if (this.ngImageGalleryPreviewInstance) {
      this.ngImageGalleryPreviewInstance.hide();
    }
  }

  static closeGallery() {
    if (NgImageGalleryPreviewDirective.ngImageGalleryPreviewComponentRef) {
      const instance = NgImageGalleryPreviewDirective.ngImageGalleryPreviewComponentRef.instance;
      if (instance) {
        instance.hide();
      }
    }
  }

  static destroyGallery() {
    if (NgImageGalleryPreviewDirective.ngImageGalleryPreviewComponentRef) {
      const appRef = NgImageGalleryPreviewDirective.ngImageGalleryPreviewComponentRef.injector.get(ApplicationRef);
      appRef.detachView(NgImageGalleryPreviewDirective.ngImageGalleryPreviewComponentRef.hostView);
      NgImageGalleryPreviewDirective.ngImageGalleryPreviewComponentRef.destroy();
      NgImageGalleryPreviewDirective.ngImageGalleryPreviewComponentRef = null;
    }
  }

  private getDomElement<T>(componentRef: ComponentRef<T>): HTMLElement {
    if (!componentRef) return;
    return (componentRef.hostView as EmbeddedViewRef<T>).rootNodes[0] as HTMLElement;
  }

  private appendToBody() {
    NgImageGalleryPreviewDirective.ngImageGalleryPreviewComponentRef = this.dynamicComponentsService.createComponentElement(
      NgImageGalleryPreviewComponent,
      {}
    );

    this.appRef.attachView(NgImageGalleryPreviewDirective.ngImageGalleryPreviewComponentRef.hostView);

    document.body.appendChild(this.getDomElement(NgImageGalleryPreviewDirective.ngImageGalleryPreviewComponentRef));
  }

}
